import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Alert,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Text, TextInput, Button, Surface } from 'react-native-paper';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RouteProp } from '@react-navigation/native';
import { RootStackParamList } from '../types/navigation';
import helpService from '../services/helpService';
import SlidingMenu from '../components/SlidingMenu';
import MaxWidthContainer from '../components/MaxWidthContainer';
import HeaderBar from '../components/HeaderBar';
import { brandPrimary, brandPrimarySoft, brandButton } from '../theme/paperTheme';

type HelpResponseScreenNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'HelpResponse'
>;
type HelpResponseScreenRouteProp = RouteProp<RootStackParamList, 'HelpResponse'>;

type HelpResponseScreenProps = {
  navigation: HelpResponseScreenNavigationProp;
  route: HelpResponseScreenRouteProp;
};

const HelpResponseScreen: React.FC<HelpResponseScreenProps> = ({ navigation, route }) => {
  const { helpRequest } = route.params;
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;

  const [responseText, setResponseText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleSubmit = async () => {
    if (!responseText.trim()) {
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
      return;
    }

    try {
      setIsLoading(true);
      await helpService.addResponse(helpRequest._id, responseText);
      setResponseText('');
      setShowSuccess(true);
      setIsLoading(false);
      setTimeout(() => {
        navigation.navigate('ViewResponses', {
          helpRequest,
          shouldRefresh: true,
        });
      }, 2000);
    } catch (error) {
      setIsLoading(false);
      Alert.alert('Error', 'Failed to submit response. Please try again.');
      console.error('Error submitting response:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  return (
    <View style={styles.container}>
      <Surface style={styles.surface}>
        <View style={styles.headerWrap}>
          <HeaderBar
            rightContent={
              <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
                <Text style={styles.menuText}>☰</Text>
              </TouchableOpacity>
            }
            onLogoPress={() => navigation.navigate('Home')}
            style={styles.headerBar}
          />
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          <ScrollView
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
          >
            <Text style={styles.screenLabel}>Offer support</Text>
            <Text style={styles.screenTitle}>Share your guidance</Text>
            <Text style={styles.screenSubtitle}>
              Your response will appear anonymously to the community.
            </Text>

            <Surface style={styles.questionCard} elevation={0}>
              <Text style={styles.questionTitle}>{helpRequest.title}</Text>
              {helpRequest.category ? (
                <Text style={styles.questionCategory}>{helpRequest.category}</Text>
              ) : null}
              <Text style={styles.questionDescription}>{helpRequest.description}</Text>
              <View style={styles.questionMeta}>
                <Text style={styles.questionAuthor}>{helpRequest.user.displayName}</Text>
                <Text style={styles.questionDate}>
                  {new Date(helpRequest.createdAt).toLocaleDateString()}
                </Text>
              </View>
            </Surface>

            <Surface style={styles.responseCard} elevation={0}>
              <Text style={styles.responseLabel}>Your response</Text>
              <TextInput
                mode="outlined"
                multiline
                numberOfLines={6}
                value={responseText}
                onChangeText={setResponseText}
                placeholder="Type your response here…"
                placeholderTextColor="#94A3B8"
                style={styles.responseInput}
                outlineColor="rgba(15, 23, 42, 0.12)"
                activeOutlineColor={brandPrimary}
              />
              <Button
                mode="contained"
                onPress={handleSubmit}
                loading={isLoading}
                disabled={isLoading || showSuccess}
                style={styles.submitButton}
                buttonColor={brandButton}
                textColor="#ffffff"
              >
                Submit response
              </Button>
              {showSuccess && (
                <Text style={styles.successMessage}>Your response has been submitted!</Text>
              )}
              {showError && (
                <Text style={styles.errorMessage}>
                  Please complete the response field before submitting
                </Text>
              )}
            </Surface>
          </ScrollView>
        </MaxWidthContainer>
      </Surface>

      <SlidingMenu isVisible={isMenuVisible} onClose={toggleMenu} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
  },
  screenLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  screenTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1E293B',
    letterSpacing: -0.5,
    marginBottom: 8,
    lineHeight: 36,
  },
  screenSubtitle: {
    fontSize: 16,
    color: '#64748B',
    lineHeight: 24,
    marginBottom: 24,
  },
  questionCard: {
    padding: 20,
    borderRadius: 16,
    marginBottom: 20,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.06,
          shadowRadius: 12,
          elevation: 3,
        }),
  },
  questionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 10,
    lineHeight: 26,
  },
  questionCategory: {
    fontSize: 12,
    fontWeight: '600',
    color: brandPrimary,
    backgroundColor: brandPrimarySoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    alignSelf: 'flex-start',
    marginBottom: 12,
    overflow: 'hidden',
  },
  questionDescription: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
    marginBottom: 14,
  },
  questionMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  questionAuthor: {
    fontSize: 13,
    color: '#64748B',
    fontWeight: '500',
  },
  questionDate: {
    fontSize: 13,
    color: '#94A3B8',
  },
  responseCard: {
    padding: 24,
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.06,
          shadowRadius: 12,
          elevation: 3,
        }),
  },
  responseLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 14,
  },
  responseInput: {
    backgroundColor: '#FFFFFF',
    marginBottom: 20,
    minHeight: 140,
  },
  submitButton: {
    borderRadius: 999,
    alignSelf: 'center',
    paddingHorizontal: 24,
  },
  successMessage: {
    color: brandPrimary,
    textAlign: 'center',
    marginTop: 14,
    fontSize: 15,
    fontWeight: '600',
  },
  errorMessage: {
    color: '#d32f2f',
    textAlign: 'center',
    marginTop: 14,
    fontSize: 15,
    fontWeight: '600',
  },
});

export default HelpResponseScreen;
